import { useState, useEffect, useCallback, useMemo } from 'react';
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";
import Logger from "../../../basecomp/base/Logger";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

// Base Url Setup
const { axiosGetCall } = AxiosWrapper;
const REACT_BE_URL = process.env.REACT_APP_BE_URL;

// Geographic center coordinates for auto-zoom [lat, lng, zoom_level]
const GEOGRAPHIC_CENTERS = {
  // Countries
  'IN': [20.5937, 78.9629, 5],
  'US': [37.0902, -95.7129, 4],
  'CN': [35.8617, 104.1954, 4],
  'JP': [36.2048, 138.2529, 5],
  'GB': [55.3781, -3.4360, 6],
  'DE': [51.1657, 10.4515, 6],
  'FR': [46.2276, 2.2137, 6],
  'NL': [52.1326, 5.2913, 7],
  'SG': [1.3521, 103.8198, 11],
  'AE': [23.4241, 53.8478, 7],
  'AU': [-25.2744, 133.7751, 4],
  'BR': [-14.2350, -51.9253, 4],
  'ZA': [-30.5595, 22.9375, 5],
  'MX': [23.6345, -102.5528, 5],
  'CA': [56.1304, -106.3468, 4],
  'IT': [41.8719, 12.5674, 6],
  'ES': [40.4637, -3.7492, 6],
  'AT': [47.5162, 14.5501, 7],
  'BE': [50.5039, 4.4699, 8],
  'AR': [-38.4161, -63.6167, 4],
  'CL': [-35.6751, -71.5430, 4],
  'EG': [26.8206, 30.8025, 6],
  'NG': [9.0820, 8.6753, 6],
  'NZ': [-40.9006, 174.8860, 5],
  'AZ': [40.1431, 47.5769, 7],
  'BS': [25.0343, -77.3963, 8],
  // Continents
  'AS': [34.0479, 100.6197, 3],
  'EU': [54.5260, 15.2551, 4],
  'NA': [54.5260, -105.2551, 3],
  'SA': [-8.7832, -55.4915, 3],
  'AF': [-8.7832, 34.5085, 3],
  'OC': [-22.7359, 140.0188, 3],
  'AN': [-75.2509, -0.0713, 2],
};

const ROLE_TYPES = [
  'Seaport',
  'Rail Terminal',
  'Road Terminal',
  'Airport',
  'Postal Exchange',
  'Intermodal Terminal',
  'Fixed Transport Function',
  'Border Crossing Function'
];

const CreateGLNVM = () => {
  const { handleToastOpen } = useToast();

  // Helper function to extract the last part of a hierarchical code
  const extractLastPart = useCallback((code) => {
    if (!code) return null;
    const parts = code.split('_');
    return parts[parts.length - 1];
  }, []);

  // Base GLN functionality
  const [baseLoading, setBaseLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  
  // Updated formData with GLN ID field and wasUpdated flag
  const [formData, setFormData] = useState({
    gln_id: '',
    code_type: '',
    code_suffix: '',
    gln_type: 'fixed_physical_location',
    facility_name: '',
    role_type: '',
    country: '',
    continent: '',
    region: '',
    city: '',
    unlocode: '',
    latitude: '',
    longitude: '',
    gln_status: 'active',
    data_source: 'Manual Entry',
    wasUpdated: false
  });

  // Geographic data
  const [countries, setCountries] = useState([]);
  const [continents, setContinents] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);

  // Local state for the creation process
  const [activeStep, setActiveStep] = useState(0);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userCanCreate, setUserCanCreate] = useState(true);

  // Combined loading state
  const isLoading = baseLoading || isSubmitting;

  // IMPORTANT: Define convertRoleTypeToArray BEFORE it's used in handleAddRecord
  const convertRoleTypeToArray = useCallback((roleType) => {
    if (!roleType) {
      return '0,0,0,0,0,0,0,0'; // All zeros if no role type selected
    }
    
    const roleTypeArray = new Array(8).fill('0');
    const roleTypeIndex = ROLE_TYPES.indexOf(roleType);
    
    if (roleTypeIndex !== -1) {
      roleTypeArray[roleTypeIndex] = '1';
    }
    
    return roleTypeArray.join(',');
  }, []);

  // Get user email helper
  const getUserEmail = useCallback(() => {
    return localStorage.getItem('email') || sessionStorage.getItem('email');
  }, []);

  // Load user profile and geographic data on mount
  useEffect(() => {
    loadUserProfile();
    loadCountries();
    loadContinents();
  }, []);

  // Load regions when country changes
  useEffect(() => {
    if (formData.country) {
      loadRegions(formData.country);
      
      // Extract continent from hierarchical country code
      let continent = 'AS'; // Default fallback
      
      if (formData.country.includes('_')) {
        const countryParts = formData.country.split('_');
        if (countryParts.length >= 2) {
          continent = countryParts[0];
          console.log("Extracted continent from country code:", continent, "from", formData.country);
        }
      } else {
        const country = countries.find(c => c.code === formData.country);
        if (country && country.continent_code) {
          continent = country.continent_code;
        } else {
          const continentMapping = {
            'IN': 'AS', 'CN': 'AS', 'JP': 'AS', 'SG': 'AS', 'AE': 'AS',
            'US': 'NA', 'CA': 'NA', 'MX': 'NA', 'BS': 'NA',
            'GB': 'EU', 'DE': 'EU', 'FR': 'EU', 'NL': 'EU', 'IT': 'EU', 'AT': 'EU', 'BE': 'EU',
            'BR': 'SA', 'AR': 'SA', 'CL': 'SA',
            'AU': 'OC', 'NZ': 'OC',
            'ZA': 'AF', 'EG': 'AF', 'NG': 'AF'
          };
          const countryCode = extractLastPart(formData.country) || formData.country;
          continent = continentMapping[countryCode] || 'AS';
        }
      }
      
      setFormData(prev => ({
        ...prev,
        continent
      }));
    } else {
      setRegions([]);
      setCities([]);
      setFormData(prev => ({
        ...prev,
        continent: '',
        region: '',
        city: ''
      }));
    }
  }, [formData.country, countries, extractLastPart]);

  // Load cities when region changes
  useEffect(() => {
    if (formData.region) {
      loadCities(formData.region);
    } else {
      setCities([]);
      setFormData(prev => ({
        ...prev,
        city: ''
      }));
    }
  }, [formData.region]);

  // Auto-zoom map when geographic location is selected
  useEffect(() => {
    if (formData.country || formData.continent) {
      let targetLocation = null;
      
      // Priority: Use existing coordinates if available, otherwise use geographic centers
      if (formData.latitude && formData.longitude) {
        return; // Don't override user-set coordinates
      }
      
      if (formData.country) {
        const countryCode = extractLastPart(formData.country) || formData.country;
        if (GEOGRAPHIC_CENTERS[countryCode]) {
          const [lat, lng] = GEOGRAPHIC_CENTERS[countryCode];
          targetLocation = [lat, lng];
        }
      } else if (formData.continent) {
        if (GEOGRAPHIC_CENTERS[formData.continent]) {
          const [lat, lng] = GEOGRAPHIC_CENTERS[formData.continent];
          targetLocation = [lat, lng];
        }
      }
      
      if (targetLocation) {
        setMapPosition(targetLocation);
      }
    }
  }, [formData.country, formData.continent, formData.latitude, formData.longitude, extractLastPart]);

  // Load user profile function
  const loadUserProfile = useCallback(async () => {
    setBaseLoading(true);
    try {
      const userEmail = getUserEmail();
      
      if (!userEmail) {
        throw new Error('User email not found - user not authenticated');
      }

      console.log("Fetching user organization for email:", userEmail);
      
      const response = await axiosGetCall(`/gln/getUserOrganization?email=${encodeURIComponent(userEmail)}`);
      console.log("User organization response:", response.data);
      
      if (response.status === 200) {
        if (response.data.success && response.data.data) {
          console.log("User profile loaded successfully");
          setUserProfile(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to load user profile');
        }
      } else {
        throw new Error(`HTTP ${response.status}: Failed to load user profile`);
      }
    } catch (error) {
      Logger.printStackTrace("Error loading user profile:", error);
    } finally {
      setBaseLoading(false);
    }
  }, [getUserEmail]);

  // Load countries
  const loadCountries = useCallback(async () => {
    try {
      console.log("Fetching countries list");
      
      const response = await axiosGetCall('/gln/getCountriesList');
      console.log("Countries response:", response.data);
      
      if (response.status === 200) {
        if (response.data.success && response.data.data) {
          console.log("Countries loaded successfully");
          setCountries(response.data.data);
        } else if (response.data.status === undefined || response.data.status === 200) {
          setCountries(response.data);
        } else {
          throw new Error(response.data.message || 'Failed to load countries');
        }
      }
    } catch (error) {
      Logger.printStackTrace("Error loading countries:", error);
      console.log("Using fallback countries data");
      setCountries([
        { code: 'IN', name: 'India', continent_code: 'AS' },
        { code: 'NL', name: 'Netherlands', continent_code: 'EU' },
        { code: 'US', name: 'United States', continent_code: 'NA' },
        { code: 'GB', name: 'United Kingdom', continent_code: 'EU' },
        { code: 'DE', name: 'Germany', continent_code: 'EU' },
        { code: 'FR', name: 'France', continent_code: 'EU' },
        { code: 'AT', name: 'Austria', continent_code: 'EU' },
        { code: 'SG', name: 'Singapore', continent_code: 'AS' },
        { code: 'AE', name: 'United Arab Emirates', continent_code: 'AS' },
        { code: 'CN', name: 'China', continent_code: 'AS' },
        { code: 'JP', name: 'Japan', continent_code: 'AS' },
      ]);
    }
  }, []);

  // Load continents
  const loadContinents = useCallback(async () => {
    try {
      console.log("Fetching continents list");
      
      const response = await axiosGetCall('/gln/getUniqueContinentCodes');
      console.log("Continents response:", response.data);
      
      if (response.status === 200) {
        if (response.data.success && response.data.data) {
          console.log("Continents loaded successfully");
          setContinents(response.data.data);
        } else if (response.data.status === undefined || response.data.status === 200) {
          const mappedData = Array.isArray(response.data) ? response.data : [];
          setContinents(mappedData);
        } else {
          throw new Error(response.data.message || 'Failed to load continents');
        }
      }
    } catch (error) {
      Logger.printStackTrace("Error loading continents:", error);
      console.log("Using fallback continents data");
      setContinents([
        { continent_code: 'AS', continent_name: 'Asia' },
        { continent_code: 'EU', continent_name: 'Europe' },
        { continent_code: 'NA', continent_name: 'North America' },
        { continent_code: 'SA', continent_name: 'South America' },
        { continent_code: 'AF', continent_name: 'Africa' },
        { continent_code: 'OC', continent_name: 'Oceania' },
        { continent_code: 'AN', continent_name: 'Antarctica' }
      ]);
    }
  }, []);

  // Load regions for a specific country
  const loadRegions = useCallback(async (countryCode) => {
    try {
      console.log("Fetching regions for country:", countryCode);
      
      const response = await axiosGetCall(`/gln/searchRegionCodes?countryCodes=${countryCode}&searchInput=`);
      console.log("Regions response:", response);
      
      if (response.status === 200) {
        let regionsData = [];
        
        if (response.data && response.data.success && response.data.data) {
          console.log("Regions loaded successfully (new format)");
          regionsData = response.data.data;
        } else if (response.data && (response.data.status === undefined || response.data.status === 200)) {
          regionsData = Array.isArray(response.data) ? response.data : [];
          console.log("Regions loaded successfully (legacy format)");
        } else if (Array.isArray(response.data)) {
          regionsData = response.data;
          console.log("Regions loaded successfully (direct array)");
        } else {
          console.warn("Unexpected regions response format:", response.data);
          regionsData = [];
        }
        
        console.log("Final regions data:", regionsData.length, "items:", regionsData.slice(0, 3));
        setRegions(regionsData);
      } else {
        console.warn("Non-200 status for regions:", response.status);
        setRegions([]);
      }
    } catch (error) {
      Logger.printStackTrace("Error loading regions:", error);
      console.warn("Regions API failed, using empty array");
      setRegions([]);
    }
  }, []);

  // Load cities for a specific region
  const loadCities = useCallback(async (regionCode) => {
    try {
      console.log("Fetching cities for region:", regionCode);
      
      const response = await axiosGetCall(
        `/gln/searchCityCodes?regionCodes=${regionCode}&countryCodes=${formData.country}&searchInput=`
      );
      console.log("Cities response:", response.data);
      
      if (response.status === 200) {
        let citiesData = [];
        
        if (response.data.success && response.data.data) {
          console.log("Cities loaded successfully (new format)");
          citiesData = response.data.data;
        } else if (response.data.status === undefined || response.data.status === 200) {
          citiesData = Array.isArray(response.data) ? response.data : [];
          console.log("Cities loaded successfully (legacy format)");
        } else {
          throw new Error(response.data.message || 'Failed to load cities');
        }
        
        console.log("Final cities data:", citiesData);
        setCities(citiesData);
      }
    } catch (error) {
      Logger.printStackTrace("Error loading cities:", error);
      setCities([]);
    }
  }, [formData.country]);

  // Build the GLN code: CONTINENT_COUNTRY_REGION_CITY_CODESUFFIX (user provides unique suffix)
  const builtCode = useMemo(() => {
    const parts = [];
    
    console.log("Building code with raw data:", {
      continent: formData.continent,
      country: formData.country,
      region: formData.region,
      city: formData.city,
      code_suffix: formData.code_suffix
    });
    
    if (formData.continent) {
      parts.push(formData.continent);
    }
    
    if (formData.country) {
      const countryCode = extractLastPart(formData.country);
      if (countryCode) {
        parts.push(countryCode);
      }
    }
    
    if (formData.region) {
      const regionCode = extractLastPart(formData.region);
      if (regionCode) {
        parts.push(regionCode);
      }
    }
    
    if (formData.city) {
      const cityCode = extractLastPart(formData.city);
      if (cityCode) {
        parts.push(cityCode);
      }
    }
    
    if (formData.code_suffix) {
      parts.push(formData.code_suffix);
    }
    
    const result = parts.length > 0 ? parts.join('_') : '';
    console.log("Built final code:", result, "from parts:", parts);
    
    return result;
  }, [formData.continent, formData.country, formData.region, formData.city, formData.code_suffix, extractLastPart]);

  // Extract subregion from UNLOCODE
  const extractSubregionFromUnlocode = useCallback(async (unlocode) => {
    if (!unlocode || unlocode.length < 3) return;
    
    try {
      const countryFromUnlocode = unlocode.substring(0, 2);
      const subregionFromUnlocode = unlocode.substring(2);
      
      console.log("Extracting from UNLOCODE:", { unlocode, countryFromUnlocode, subregionFromUnlocode });
      
      if (countryFromUnlocode === formData.country && subregionFromUnlocode.length > 0) {
        const matchingCity = cities.find(city => {
          const cityCode = city.cityCode || city.city_code;
          return cityCode && cityCode.toLowerCase() === subregionFromUnlocode.toLowerCase();
        });
        
        if (matchingCity) {
          console.log("Found matching city:", matchingCity);
          const cityCode = matchingCity.cityCode || matchingCity.city_code;
          setFormData(prev => ({
            ...prev,
            city: cityCode
          }));
        }
      }
      
      if (countryFromUnlocode !== formData.country) {
        const matchingCountry = countries.find(country => 
          country.code === countryFromUnlocode
        );
        
        if (matchingCountry) {
          console.log("Updating country from UNLOCODE:", matchingCountry);
          setFormData(prev => ({
            ...prev,
            country: countryFromUnlocode
          }));
        }
      }
    } catch (error) {
      Logger.printStackTrace("Error extracting subregion from UNLOCODE:", error);
    }
  }, [formData.country, cities, countries]);

  // Form change handler
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Reset form data
  const resetFormData = useCallback(() => {
    setFormData({
      gln_id: '',
      code_type: '',
      code_suffix: '',
      gln_type: 'fixed_physical_location',
      facility_name: '',
      role_type: '',
      country: '',
      continent: '',
      region: '',
      city: '',
      unlocode: '',
      latitude: '',
      longitude: '',
      gln_status: 'active',
      data_source: 'Manual Entry',
      wasUpdated: false
    });
  }, []);

  // Validate code function
  const handleValidateCode = useCallback(async (codeData) => {
    try {
      console.log("Validating GLN code:", codeData);
      
      const response = await axios.post(
        `${REACT_BE_URL}/gln/validateGlnCode`,
        {
          generated_code: codeData.generated_code,
          code_type: codeData.code_type
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.status === 200) {
        if (response.data.success) {
          console.log("GLN code validation completed:", response.data);
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Validation failed');
        }
      }
    } catch (error) {
      Logger.printStackTrace("Validation error:", error);
      
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      throw new Error(`Validation failed: ${error.message}`);
    }
  }, []);

  // Add record function - handles both create and update
  const handleAddRecord = useCallback(async (recordData) => {
    setBaseLoading(true);
    try {
      const userEmail = getUserEmail();
      
      // Convert role_type to array format
      const roleTypeArray = convertRoleTypeToArray(recordData.role_type);
      
      const payload = {
        ...recordData,
        user_email: userEmail,
        generated_code: builtCode,
        gln_id: recordData.gln_id || undefined,
        data_source: 'Manual Entry',
        role_type: roleTypeArray // Use converted array format
      };
      
      console.log("Creating/Updating GLN record with payload:", payload);
      console.log("Role type converted to:", roleTypeArray);
      
      const response = await axios.post(
        `${REACT_BE_URL}/gln/createGlnRecord`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      
      // Check both status AND success flag
      if ((response.status === 201 || response.status === 200) && response.data.success) {
        console.log("GLN Record processed successfully:", response.data);
        
        const returnedData = response.data.data;
        const action = response.data.action || (response.status === 200 ? 'updated' : 'created');
        const wasUpdated = action === 'updated';
        
        setFormData(prev => ({
          ...prev,
          gln_id: returnedData.gln_id,
          generated_code: returnedData.generated_code,
          wasUpdated: wasUpdated
        }));
        
        handleToastOpen('success', `GLN record ${action} successfully! Code: ${returnedData.generated_code}`);
        
        return { ...returnedData, wasUpdated };
      } else {
        throw new Error(response.data.message || 'Failed to process GLN record');
      }
    } catch (error) {
      Logger.printStackTrace("Error processing GLN record:", error);
      
      let errorMessage = 'Failed to process GLN record';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      handleToastOpen('error', errorMessage);
      throw new Error(errorMessage);
    } finally {
      setBaseLoading(false);
    }
  }, [builtCode, getUserEmail, handleToastOpen, convertRoleTypeToArray]);

  // Permission check function
  const canCreate = useCallback(async () => {
    try {
      const userEmail = getUserEmail();
      
      if (!userEmail) {
        console.log("No user email found, cannot create GLN");
        return false;
      }

      console.log("Permission check passed for user:", userEmail);
      return true;
    } catch (error) {
      Logger.printStackTrace("Error checking permissions:", error);
      return false;
    }
  }, [getUserEmail]);

  // Check permissions when country or gln_type changes
  useEffect(() => {
    const checkPermissions = async () => {
      if (formData.country || formData.gln_type) {
        const canCreateGLN = await canCreate();
        setUserCanCreate(canCreateGLN);
      }
    };
    
    checkPermissions();
  }, [formData.country, formData.gln_type, canCreate]);

  // Initialize form on mount
  useEffect(() => {
    initializeForm();
  }, []);

  // Initialize form with default values
  const initializeForm = useCallback(() => {
    resetFormData();
    setActiveStep(0);
    setValidationResult(null);
    setFormErrors({});
    setMapPosition(null);
    setShowSuccess(false);
  }, [resetFormData]);

  // Validation function
  const validateCurrentStep = useCallback(() => {
    const errors = {};
    
    if (formData.gln_id && !/^\d{13}$/.test(formData.gln_id)) {
      errors.gln_id = 'GLN ID must be exactly 13 digits';
    }
    
    if (!formData.code_type) errors.code_type = 'Code Type is required';
    if (!formData.code_suffix) errors.code_suffix = 'Code Suffix is required';
    if (!formData.country) errors.country = 'Country is required';
    
    if (formData.latitude && (isNaN(formData.latitude) || formData.latitude < -90 || formData.latitude > 90)) {
      errors.latitude = 'Latitude must be between -90 and 90';
    }
    if (formData.longitude && (isNaN(formData.longitude) || formData.longitude < -180 || formData.longitude > 180)) {
      errors.longitude = 'Longitude must be between -180 and 180';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Handle code validation
  const performCodeValidation = useCallback(async () => {
    if (!builtCode || !formData.code_type) {
      return;
    }

    setIsValidating(true);
    try {
      const result = await handleValidateCode({
        generated_code: builtCode,
        code_type: formData.code_type
      });
      setValidationResult(result);
    } catch (error) {
      Logger.printStackTrace("Code validation failed:", error);
      setValidationResult({
        isValid: false,
        exists: false,
        isValidFormat: false,
        formatError: error.message
      });
    } finally {
      setIsValidating(false);
    }
  }, [builtCode, formData.code_type, handleValidateCode]);

  // Map dialog handlers
  const openMapDialog = useCallback(() => {
    setMapDialogOpen(true);
  }, []);

  const closeMapDialog = useCallback(() => {
    setMapDialogOpen(false);
  }, []);

  const handleMapConfirm = useCallback(() => {
    if (mapPosition) {
      handleFormChange('latitude', mapPosition[0].toString());
      handleFormChange('longitude', mapPosition[1].toString());
    }
    closeMapDialog();
  }, [mapPosition, handleFormChange]);

  const updateMapPosition = useCallback((position) => {
    setMapPosition(position);
  }, []);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      console.log("Getting current location...");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPosition = [position.coords.latitude, position.coords.longitude];
          console.log("Current location obtained:", newPosition);
          setMapPosition(newPosition);
          handleFormChange('latitude', position.coords.latitude.toString());
          handleFormChange('longitude', position.coords.longitude.toString());
          resolve(newPosition);
        },
        (error) => {
          Logger.printStackTrace("Error getting location:", error);
          reject(new Error('Unable to get your current location. Please ensure location services are enabled.'));
        }
      );
    });
  }, [handleFormChange]);

  // Handle form submission
  const handleSubmit = useCallback(async (onSuccess, onError) => {
    console.log("Starting form submission...");
    
    if (!validateCurrentStep()) {
      console.log("Form validation failed");
      return false;
    }

    const hasPermission = await canCreate();
    if (!hasPermission) {
      console.log("Permission check failed");
      if (onError) {
        onError(new Error('You do not have permission to create GLN records with these parameters'));
      }
      return false;
    }

    setIsSubmitting(true);
    try {
      const result = await handleAddRecord(formData);
      console.log("GLN record processed successfully");
      setShowSuccess(true);
      
      // No automatic redirect - user must click the back button
      
      return true;
    } catch (error) {
      Logger.printStackTrace("Error processing GLN record:", error);
      if (onError) {
        onError(error);
      }
      return false;
    } finally {
      setIsSubmitting(false);}
  }, [validateCurrentStep, formData, canCreate, handleAddRecord]);

  // Handle cancel
  const handleCancel = useCallback((onCancel) => {
    console.log("Form cancelled");
    initializeForm();
    if (onCancel) {
      onCancel();
    }
  }, [initializeForm]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    console.log("Clearing all form data");
    setFormData({
      gln_id: '',
      code_type: '',
      code_suffix: '',
      gln_type: 'fixed_physical_location',
      facility_name: '',
      role_type: '',
      country: '',
      continent: '',
      region: '',
      city: '',
      unlocode: '',
      latitude: '',
      longitude: '',
      gln_status: 'active',
      data_source: 'Manual Entry',
      wasUpdated: false
    });
    
    setFormErrors({});
    setValidationResult(null);
    setMapPosition(null);
  }, []);

  // Get validation icon state
  const getValidationIconState = useCallback(() => {
    if (isValidating) {
      return 'loading';
    }
    if (validationResult === null) {
      return 'none';
    }
    return validationResult.isValid ? 'success' : 'error';
  }, [isValidating, validationResult]);

  // Get country name by code
  const getCountryName = useCallback((countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  }, [countries]);

  // Enhanced form change handler
  const enhancedFormChange = useCallback((field, value) => {
    console.log(`Form field changed: ${field} = ${value}`);
    handleFormChange(field, value);
    
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [formErrors, handleFormChange]);

  // Update coordinates from map
  const updateCoordinatesFromMap = useCallback((position) => {
    if (position && position.length === 2) {
      console.log("Coordinates updated from map:", position);
      handleFormChange('latitude', position[0].toString());
      handleFormChange('longitude', position[1].toString());
      setMapPosition(position);
    }
  }, [handleFormChange]);

  // Get map zoom level based on selection
  const getMapZoomLevel = useCallback(() => {
    if (formData.city) return 13;
    if (formData.region) return 9;
    if (formData.country) {
      const countryCode = extractLastPart(formData.country) || formData.country;
      if (['SG', 'BE', 'NL'].includes(countryCode)) return 9;
      return 6;
    }
    if (formData.continent) return 4;
    return 5;
  }, [formData.city, formData.region, formData.country, formData.continent, extractLastPart]);

  return {
    // State
    activeStep,
    mapDialogOpen,
    mapPosition,
    validationResult,
    isValidating,
    formErrors,
    isSubmitting,
    showSuccess,
    isLoading,
    
    // Data
    countries,
    continents,
    regions,
    cities,
    formData,
    userProfile,
    builtCode,
    
    // Actions
    openMapDialog,
    closeMapDialog,
    handleMapConfirm,
    updateMapPosition,
    getCurrentLocation,
    handleSubmit,
    handleCancel,
    handleClearAll,
    performCodeValidation,
    enhancedFormChange,
    updateCoordinatesFromMap,
    extractSubregionFromUnlocode,
    
    // Computed values
    getValidationIconState,
    getCountryName,
    getMapZoomLevel,
    
    // Permissions
    canCreate: () => userCanCreate,
    
    // Role type utilities
    convertRoleTypeToArray,
    ROLE_TYPES,
  };
};

export default CreateGLNVM;